import { TransactionRow } from "./TransactionRow";
import { BreadTransaction } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
  createContext,
} from "react";
import { ScrollContainerContext } from "../rye/RyeMainContentContainerBase";
import { TransactionSelectionContext } from "./TransactionSelectionContext";

const TRANSACTION_ROW_HEIGHT = 40; // h-10 = 40px
const BUFFER_SIZE = 10; // Extra rows to render above/below visible area

// This is a bit of a hack. The TransactionsTable component relies on
// the TransactionsNavContext, defined in the TransactionsScreen component,
// for the sorts & filters. This makes the TransactionsTable component less
// re-usable, but for now our other use-cases for the TransactionsTable don't
// really need sorting or filtering so we just provide an option to disable them.
export const DisableFilteringAndSortingContext = createContext<boolean>(false);

/**
 * How far the table reaches into the surrounding padding, offsetting its rows'
 * own 8px of padding, for column headers drawn outside it to line up with its
 * columns.
 */
export const TRANSACTIONS_TABLE_INSET = "-mx-2";

export function TransactionsTable({
  sortedFilteredTransactions,
  disableFilteringAndSorting = false, // assumes sortedFilteredTransactions is already sorted by date descending
  bottomBufferHeight = "lg", // height of the bottom buffer
  onSelectionCountChange,
  onSelectedIdsChange,
  deselectAllRef,
}: {
  sortedFilteredTransactions: BreadTransaction[];
  disableFilteringAndSorting?: boolean;
  bottomBufferHeight?: "sm" | "lg";
  onSelectionCountChange?: (count: number) => void;
  onSelectedIdsChange?: (ids: Set<string>) => void;
  deselectAllRef?: React.MutableRefObject<(() => void) | null>;
}): JSX.Element {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollElement = useContext(ScrollContainerContext);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });

  const updateVisibleRange = useCallback(() => {
    if (!scrollElement) return;

    // Calculate which transactions should be visible
    const startIndex = Math.max(
      0,
      Math.floor(scrollElement.scrollTop / TRANSACTION_ROW_HEIGHT) -
        BUFFER_SIZE,
    );
    const endIndex = Math.min(
      sortedFilteredTransactions.length,
      Math.ceil(
        (scrollElement.scrollTop + scrollElement.clientHeight) /
          TRANSACTION_ROW_HEIGHT,
      ) + BUFFER_SIZE,
    );

    setVisibleRange({ start: startIndex, end: endIndex });
  }, [scrollElement, sortedFilteredTransactions.length]);

  useEffect(() => {
    if (!scrollElement) return;

    // Initial calculation
    updateVisibleRange();

    // Add scroll listener to the context-provided scroll element
    scrollElement.addEventListener("scroll", updateVisibleRange, {
      passive: true,
    });

    // Add resize listener for window resize
    window.addEventListener("resize", updateVisibleRange);

    return () => {
      scrollElement.removeEventListener("scroll", updateVisibleRange);
      window.removeEventListener("resize", updateVisibleRange);
    };
  }, [scrollElement, updateVisibleRange]);

  // Selection state — lifted here so it persists across virtualization unmounts
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cursorId, setCursorId] = useState<string | null>(null);
  const anchorIdRef = useRef<string | null>(null);
  const selectionHandledRef = useRef(false);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null,
  );

  // Use a ref so selection callbacks don't depend on the transactions array
  const sortedTransactionsRef = useRef(sortedFilteredTransactions);
  sortedTransactionsRef.current = sortedFilteredTransactions;

  const cursorIndex =
    cursorId !== null
      ? sortedFilteredTransactions.findIndex((t) => t.id === cursorId)
      : -1;
  const cursorIndexRef = useRef(cursorIndex);
  cursorIndexRef.current = cursorIndex;

  // Remove selected/cursor IDs that are no longer in the filtered list
  useEffect(() => {
    if (selectedIds.size === 0 && cursorId === null) return;

    const validIds = new Set(sortedFilteredTransactions.map((t) => t.id));

    let changed = false;
    const newSelectedIds = new Set<string>();
    selectedIds.forEach((id) => {
      if (validIds.has(id)) {
        newSelectedIds.add(id);
      } else {
        changed = true;
      }
    });
    if (changed) {
      setSelectedIds(newSelectedIds);
    }

    if (cursorId !== null && !validIds.has(cursorId)) {
      setCursorId(null);
      anchorIdRef.current = null;
    }
  }, [sortedFilteredTransactions, selectedIds, cursorId]);

  // Notify parent of selection count changes
  useEffect(() => {
    onSelectionCountChange?.(selectedIds.size);
    onSelectedIdsChange?.(selectedIds);
  }, [selectedIds, onSelectionCountChange, onSelectedIdsChange]);

  // Focus the pending row element after it renders into the visible range
  useEffect(() => {
    if (pendingFocusIndex === null) return;
    const el = document.querySelector(
      `[data-transaction-row="${pendingFocusIndex}"]`,
    ) as HTMLDivElement;
    if (el) {
      el.focus();
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, visibleRange]);

  // Select a single transaction (click without modifiers, or arrow key)
  const selectSingle = useCallback((index: number) => {
    const transactions = sortedTransactionsRef.current;
    if (index < 0 || index >= transactions.length) return;
    const id = transactions[index].id;
    setSelectedIds(new Set([id]));
    setCursorId(id);
    anchorIdRef.current = id;
  }, []);

  // Toggle a single transaction in/out of the selection (cmd/ctrl + click)
  const toggleSelect = useCallback((index: number) => {
    const transactions = sortedTransactionsRef.current;
    if (index < 0 || index >= transactions.length) return;
    const id = transactions[index].id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setCursorId(id);
    anchorIdRef.current = id;
  }, []);

  // Select range from anchor to toIndex (shift + click or shift + arrow)
  const selectRange = useCallback((toIndex: number) => {
    const transactions = sortedTransactionsRef.current;
    if (toIndex < 0 || toIndex >= transactions.length) return;

    const anchorIdx =
      anchorIdRef.current !== null
        ? transactions.findIndex((t) => t.id === anchorIdRef.current)
        : -1;

    if (anchorIdx === -1) {
      // No anchor — treat as single select
      const id = transactions[toIndex].id;
      setSelectedIds(new Set([id]));
      setCursorId(id);
      anchorIdRef.current = id;
      return;
    }

    const start = Math.min(anchorIdx, toIndex);
    const end = Math.max(anchorIdx, toIndex);
    const rangeIds = new Set(
      transactions.slice(start, end + 1).map((t) => t.id),
    );
    setSelectedIds(rangeIds);
    setCursorId(transactions[toIndex].id);
    // anchor stays the same
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setCursorId(null);
    anchorIdRef.current = null;
  }, []);

  // Expose deselectAll to parent via ref
  useEffect(() => {
    if (deselectAllRef) {
      deselectAllRef.current = deselectAll;
    }
  }, [deselectAllRef, deselectAll]);

  // DOM focus + scroll only — does not change selection
  const focusTransactionAtIndex = useCallback(
    (index: number) => {
      const transactions = sortedTransactionsRef.current;
      if (index < 0 || index >= transactions.length) return;

      // If already rendered, just focus it
      const existing = document.querySelector(
        `[data-transaction-row="${index}"]`,
      ) as HTMLDivElement;
      if (existing) {
        existing.focus();
        existing.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }

      // Scroll to approximate position and focus after render
      if (scrollElement) {
        const targetTop = index * TRANSACTION_ROW_HEIGHT;
        scrollElement.scrollTo({
          top: Math.max(0, targetTop - scrollElement.clientHeight / 2),
        });
      }
      setPendingFocusIndex(index);
    },
    [scrollElement],
  );

  // When a key is pressed and the cursor row is off-screen, scroll back to it
  useEffect(() => {
    if (cursorId === null) return;

    function handleKeyDown(event: KeyboardEvent) {
      // Ignore modifier-only keys (shift, cmd/ctrl) — the user may be
      // preparing a multi-select action, not trying to scroll back.
      if (
        event.key === "Shift" ||
        event.key === "Meta" ||
        event.key === "Control"
      ) {
        return;
      }

      // ESC always deselects, even if the cursor row is off-screen
      if (event.key === "Escape") {
        if (event.defaultPrevented) return;
        deselectAll();
        return;
      }

      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const idx = cursorIndexRef.current;
      if (idx === -1) return;

      const cursorRow = document.querySelector(
        `[data-transaction-row="${idx}"]`,
      );
      if (!cursorRow) {
        event.preventDefault();
        focusTransactionAtIndex(idx);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cursorId, focusTransactionAtIndex, deselectAll]);

  // Cmd/Ctrl+A selects all visible transactions
  useEffect(() => {
    function handleSelectAll(event: KeyboardEvent) {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "a" &&
        !event.shiftKey
      ) {
        const target = event.target as HTMLElement;
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable
        ) {
          return;
        }
        event.preventDefault();
        const allIds = new Set(sortedTransactionsRef.current.map((t) => t.id));
        setSelectedIds(allIds);
        if (sortedTransactionsRef.current.length > 0) {
          const firstId = sortedTransactionsRef.current[0].id;
          setCursorId(firstId);
          anchorIdRef.current = firstId;
        }
      }
    }

    window.addEventListener("keydown", handleSelectAll, true);
    return () => window.removeEventListener("keydown", handleSelectAll, true);
  }, []);

  // Clicking outside any transaction row deselects all
  useEffect(() => {
    if (selectedIds.size === 0 && cursorId === null) return;

    function handleMouseDown(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (target.closest("[data-transaction-row]")) return;
      setSelectedIds(new Set());
      setCursorId(null);
      anchorIdRef.current = null;
    }

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [selectedIds, cursorId]);

  // Calculate placeholder heights
  const topPlaceholderHeight = visibleRange.start * TRANSACTION_ROW_HEIGHT;
  const bottomPlaceholderHeight =
    (sortedFilteredTransactions.length - visibleRange.end) *
    TRANSACTION_ROW_HEIGHT;

  // Get visible transactions
  const visibleTransactions = sortedFilteredTransactions.slice(
    visibleRange.start,
    visibleRange.end,
  );
  const transactionRows = visibleTransactions.map((t, index) => (
    <TransactionRow
      key={t.id}
      transaction={t}
      rowIndex={visibleRange.start + index}
    />
  ));

  return (
    <DisableFilteringAndSortingContext.Provider
      value={disableFilteringAndSorting}
    >
      <TransactionSelectionContext.Provider
        value={{
          selectedTransactionIds: selectedIds,
          cursorTransactionId: cursorId,
          selectSingle,
          toggleSelect,
          selectRange,
          deselectAll,
          setCursorTransactionId: setCursorId,
          focusTransactionAtIndex,
          transactionCount: sortedFilteredTransactions.length,
          selectionHandledRef,
        }}
      >
        <div
          className={buildClasses("flex", "flex-col", TRANSACTIONS_TABLE_INSET)}
        >
          <div
            ref={scrollContainerRef}
            className={buildClasses(
              "w-full",
              "h-full",
              "flex-grow",
              "flex-shrink",
              "overflow-auto",
              "pb-56",
            )}
          >
            {/* Top placeholder for transactions above visible area */}
            {topPlaceholderHeight > 0 && (
              <div style={{ height: topPlaceholderHeight }} />
            )}

            {/* Visible transaction rows */}
            {transactionRows}

            {/* Bottom placeholder for transactions below visible area */}
            {bottomPlaceholderHeight > 0 && (
              <div style={{ height: bottomPlaceholderHeight }} />
            )}
          </div>
          <div
            className={buildClasses(
              {
                if: bottomBufferHeight === "sm",
                then: "h-16",
                else: "h-[30vh]",
              },
              "flex",
              "justify-center",
              "font-light",
              "text-on-surface-400",
            )}
          >
            End of transactions list.
          </div>
        </div>
      </TransactionSelectionContext.Provider>
    </DisableFilteringAndSortingContext.Provider>
  );
}
